/**
 * Input — one action map, two devices, identical capability.
 *
 * "There is no input on desktop that a phone cannot express" is a design
 * requirement, so both devices write the SAME `actions` object and the same
 * `intent` vector, and `inputSender` cannot tell them apart.
 *
 * ── Sprint: v1's scheme, expressed as magnitude ────────────────────────────
 *
 * v1 walked by default and sprinted while Shift was HELD (PhysicsEngine.js:264)
 * — months of muscle memory. Its one parity break was mobile, where sprint was
 * LATCHED (`keys.shift = !keys.shift`, MobileControls.js:142).
 *
 * v2 keeps v1's scheme and removes the latch. `@chaos/sim` derives speed from
 * the MAGNITUDE of the movement intent (movement.ts): WALK_INPUT is exactly
 * WALK_SPEED, 1.0 is exactly SPRINT_SPEED. WASD sends WALK_INPUT — walk by
 * default — and Shift-held sends 1.0. A stick expresses the same ramp natively
 * (feathered walks or creeps, pinned sprints), held, never latched, and
 * neither device has an input the other lacks.
 *
 * ── Buffering ─────────────────────────────────────────────────────────────
 *
 * Actions are sampled once per SIM TICK, not once per frame. A tap between two
 * ticks would otherwise be dropped on a 144Hz display and double-counted on a
 * 30Hz one, so presses latch until the next `consume()` and holds are read live.
 *
 * ── Hold to aim (desktop) ─────────────────────────────────────────────────
 *
 * Q/E/R used to cast on keydown, toward wherever the cursor happened to be —
 * "i press a button and the skill is released", with no moment to look at
 * the lane. For the AIMED skills only (`aimRules.js`: the ones whose direction
 * decides where they land — Parallel Path, Supercycle, Falcon, Starlink today,
 * derived from the hit shapes rather than named), keydown now enters aim
 * mode: the indicator shows and follows the cursor, keyup casts in the
 * direction the cursor is in at that tick, and right-click or Esc cancels
 * without casting. Skills with nothing to aim (self buffs, the auto-targeting
 * spheres, a rune at your feet) keep casting on the press, and the basic stays
 * the click. Mobile is untouched: its aimed buttons were already
 * press-drag-release, and they drive `press`/`release` directly.
 */

import { WALK_INPUT } from './constants.js';
import { holdToAimSlots } from './aimRules.js';
import { cameraState, session } from '../state.js';
import { toggleHelpOverlay, hideHelpOverlay } from '../ui/HelpOverlay.js';

/** Raw key state. Also read by the free camera. */
export const keys = {
  w: false,
  a: false,
  s: false,
  d: false,
  space: false,
  shift: false,
  ctrl: false,
};

/**
 * Held state per action. `jump` is held (the sim reads a rising edge itself and
 * uses the hold for variable jump height); the rest are edge-triggered.
 */
const held = { jump: false, dash: false, basic: false, skill1: false, skill2: false, ultimate: false };
/** Pressed-since-last-consume. Guarantees a tap survives to the next tick. */
const latched = { jump: false, dash: false, basic: false, skill1: false, skill2: false, ultimate: false };

/**
 * Which ability the reticle should be measuring.
 *
 * `AimReticle`'s own header says it exists so that YOUR range is drawn on the
 * ground BEFORE you commit — and it was reading a `_aimSlot` in main.js that
 * nothing ever assigned. So it always drew the basic attack's 2.6 units, no
 * matter which of the four you were about to cast. The feature was built,
 * wired, and silently pinned to one value.
 *
 * It lives here because both devices have to answer the same question and they
 * learn the answer at different moments: a keyboard knows on keydown, and an
 * aimed touch button knows on touchstart — which is BEFORE the press, since an
 * aimed button deliberately holds its fire until the release.
 */
/** The four slots the reticle can measure. Jump and dash are not aimed. */
const AIMED_ACTIONS = new Set(['basic', 'skill1', 'skill2', 'ultimate']);
let _aimSlot = null;

/** @param {string|null} slot */
export function setAimSlot(slot) {
  _aimSlot = slot;
}

/** The slot to draw reach for, or null to fall back to the basic. */
export function getAimSlot() {
  return _aimSlot;
}

/**
 * ── Are you actually aiming? ──────────────────────────────────────────────
 *
 * The aim indicator used to be drawn whenever you were alive: a 21-unit lane
 * on every frame of every match, which is the loudest thing on screen and
 * says nothing (you are always "aiming" somewhere). Brawl Stars shows the
 * indicator only while the thumb is on the stick. The equivalent here: an
 * aimed action HELD (attack, a skill, the ult), the touch aim stick being
 * dragged, or within AIM_LINGER_MS of the last such input — so a tap shows
 * the shape for one beat rather than one frame. Otherwise nothing.
 */
export const AIM_LINGER_MS = 250;
let _lastAimInputAt = -Infinity;

/** Records a real aim input: an aimed press or a stick drag. */
export function markAimInput(now = performance.now()) {
  _lastAimInputAt = now;
}

/** True while any aimed action is held, or a skill is being held to aim. */
export function isAimHeld() {
  return held.basic || held.skill1 || held.skill2 || held.ultimate || _aimMode !== null;
}

/**
 * The skill currently held to aim on the keyboard, or null. While set, the
 * indicator is up for it and its keyup will cast.
 */
let _aimMode = null;

/** The slots that hold to aim for the local character. Read live: the pick can change between matches. */
export function isHoldToAim(action) {
  return holdToAimSlots(session.characterId).has(action);
}

/**
 * Keydown of an ability key. An aimed skill enters aim mode instead of
 * casting; everything else presses as before. Returns what happened, for the
 * tests and nothing else.
 */
export function onActionKeyDown(action) {
  if (isHoldToAim(action)) {
    _aimMode = action;
    setAimSlot(action);
    markAimInput();
    return 'aim';
  }
  press(action);
  if (AIMED_ACTIONS.has(action)) setAimSlot(action);
  return 'press';
}

/**
 * Keyup of an ability key. If that key is the one being held to aim, this is
 * the cast: one latched press — `consumeActions` hands the next tick the bit
 * once, and `inputSender` stamps that tick with the camera's aim as it is
 * NOW, which is what "casts in the current direction" means. A cancelled aim
 * (right-click, Esc) has already cleared the mode, so its keyup is a plain
 * release.
 */
export function onActionKeyUp(action) {
  if (_aimMode === action) {
    _aimMode = null;
    press(action);
    release(action);
    setAimSlot(null);
    return 'cast';
  }
  release(action);
  // Only clear if THIS key owns the current slot. Releasing Q while E is
  // still held must not blank a reticle E is legitimately driving.
  if (action === _aimSlot) setAimSlot(null);
  return 'release';
}

/** Drops an aim in progress without casting. Returns whether there was one. */
export function cancelAim() {
  if (_aimMode === null) return false;
  _aimMode = null;
  setAimSlot(null);
  _lastAimInputAt = -Infinity;
  return true;
}

/** The skill being held to aim, for the tests and the probe. */
export function aimMode() {
  return _aimMode;
}

/**
 * The show/hide rule for the aim indicator, as a pure function of the inputs
 * so it can be tested: any aimed action held, the aim stick active, or an aim
 * input within the last AIM_LINGER_MS.
 */
export function aimIndicatorVisible({ held: anyHeld, stickActive, lastAimInputAt, now }) {
  return !!anyHeld || !!stickActive || now - lastAimInputAt < AIM_LINGER_MS;
}

/** The rule applied to the live input state. `stickActive` is the camera's aim stick. */
export function isAiming(now = performance.now(), stickActive = false) {
  return aimIndicatorVisible({ held: isAimHeld(), stickActive, lastAimInputAt: _lastAimInputAt, now });
}

/** For the probe: the inputs the rule saw. */
export function aimInputs() {
  return { held: { ...held }, lastAimInputAt: _lastAimInputAt, now: performance.now() };
}
if (typeof window !== 'undefined') window.__aimInputs = aimInputs;

/**
 * Movement intent in WORLD space: +X east, +Z south, matching the fixed
 * camera's screen axes. It used to be camera-relative and the sim rotated it by
 * the player's yaw — that is what the twin-stick pivot removed, because yaw is
 * now aim and aim is not heading.
 */
const intent = { moveX: 0, moveZ: 0 };
/** Set by MobileControls; null when the stick is not in use. */
let stick = null;

/**
 * `c` is the basic attack.
 *
 * It used to be mouse-button-0 only, which left a trackpad or keyboard-only
 * player with no way to attack at all — the one action on the bar with no key.
 * `c` sits under the left hand next to WASD and collides with nothing else the
 * client binds (WASD, Q, E, R, F, Shift, Ctrl, Space, and the gym's backtick).
 */
const KEY_TO_ACTION = {
  ' ': 'jump',
  shift: null,
  c: 'basic',
  q: 'skill1',
  e: 'skill2',
  r: 'ultimate',
  f: 'dash',
};

let _onFirstInput = null;

export function setupKeyboardInput({ onFirstInput } = {}) {
  _onFirstInput = onFirstInput ?? null;

  document.addEventListener('keydown', (e) => {
    if (e.repeat) return;
    fireFirstInput();

    const key = e.key.toLowerCase();
    if (key in keys) keys[key] = true;
    if (key === ' ') {
      keys.space = true;
      e.preventDefault();
    }
    if (e.key === 'Shift') keys.shift = true;
    if (e.key === 'Control') keys.ctrl = true;
    if (e.key === '?') toggleHelpOverlay();
    // While pointer lock is engaged the browser eats Esc to release the lock,
    // so this only ever runs with a free cursor — exactly when help is usable.
    // Esc also drops a skill held to aim, without casting it.
    if (e.key === 'Escape') {
      hideHelpOverlay();
      cancelAim();
    }

    const action = KEY_TO_ACTION[key];
    if (action) onActionKeyDown(action);
  });

  document.addEventListener('keyup', (e) => {
    const key = e.key.toLowerCase();
    if (key in keys) keys[key] = false;
    if (key === ' ') keys.space = false;
    if (e.key === 'Shift') keys.shift = false;
    if (e.key === 'Control') keys.ctrl = false;

    const action = KEY_TO_ACTION[key];
    if (action) onActionKeyUp(action);
  });

  // Mouse buttons are the basic attack and the dash. Both also have keys (C and
  // F), so a trackpad user is not locked out.
  document.addEventListener('mousedown', (e) => {
    fireFirstInput();
    // GATED ON THE CANVAS, not on pointer lock.
    //
    // Both of these used to require `cameraState.pointerLocked`, because in the
    // 3rd-person build an unlocked click was the click that ACQUIRED the lock
    // and swinging off it was a swing nobody asked for. The fixed camera never
    // locks the pointer — `initDesktopEvents` sets `pointerLocked = false` and
    // leaves it there, since capturing the cursor would take away the one thing
    // the player aims with. So the gate was permanently shut and left-click did
    // nothing at all. Reported from a laptop: the trackpad could not attack.
    //
    // The real question was never "is the pointer locked", it was "is this
    // click aimed at the game or at the UI". The canvas answers that directly,
    // and it keeps a click on the gym panel or a HUD button from burning a 6s
    // dash cooldown.
    if (!isGameSurface(e.target)) return;
    if (e.button === 0) press('basic');
    // Right-click while holding a skill to aim CANCELS the aim; the dash it
    // would otherwise be is not spent on a click that meant "never mind".
    if (e.button === 2 && !cancelAim()) press('dash');
  });
  // Release is NOT gated. A button pressed on the canvas and released after the
  // cursor wandered onto the HUD must still let go, or it stays held forever.
  document.addEventListener('mouseup', (e) => {
    if (e.button === 0) release('basic');
    if (e.button === 2) release('dash');
  });
  document.addEventListener('contextmenu', (e) => e.preventDefault());

  // A key held while the tab loses focus stays held forever otherwise, and the
  // player comes back sprinting into the zone.
  window.addEventListener('blur', releaseAll);
}

/** The 3D canvas, and nothing layered over it. */
function isGameSurface(target) {
  return target instanceof Element && target.tagName === 'CANVAS';
}

function fireFirstInput() {
  if (!_onFirstInput) return;
  const fn = _onFirstInput;
  _onFirstInput = null;
  fn();
}

/** Public so touch buttons drive exactly the same path as the keyboard. */
export function press(action) {
  fireFirstInput();
  held[action] = true;
  latched[action] = true;
  if (AIMED_ACTIONS.has(action)) markAimInput();
}

export function release(action) {
  held[action] = false;
}

export function releaseAll() {
  for (const k of Object.keys(held)) {
    held[k] = false;
  }
  for (const k of Object.keys(keys)) keys[k] = false;
  stick = null;
  _lastAimInputAt = -Infinity;
  // A skill held to aim when the tab lost focus is dropped, not cast: the
  // keyup will arrive with no cursor behind it.
  _aimMode = null;
  _aimSlot = null;
}

/** Called by MobileControls with a normalized stick vector, or null on release. */
export function setStick(vector) {
  stick = vector;
}

/**
 * The action bitfield inputs for THIS TICK.
 *
 * Consuming clears the latches, so a single tap produces exactly one tick with
 * the bit set — mashing does not queue up four casts, and a slow frame does not
 * swallow one.
 */
export function consumeActions() {
  const out = {
    jump: held.jump || latched.jump,
    dash: held.dash || latched.dash,
    basic: held.basic || latched.basic,
    skill1: held.skill1 || latched.skill1,
    skill2: held.skill2 || latched.skill2,
    ultimate: held.ultimate || latched.ultimate,
  };
  for (const k of Object.keys(latched)) latched[k] = false;
  return out;
}

/**
 * Movement intent for this tick, in WORLD space.
 *
 * The 3rd-person build sent this in the camera's frame and let the sim rotate
 * it by the player's yaw. Here yaw is aim, not facing, so rotating movement by
 * it would make you orbit whatever you point at instead of walking where you
 * asked. The camera never rotates, so "screen up" is always -Z and the client
 * can hand the sim a world vector directly.
 *
 * The stick wins when it is active: on a touch device the keyboard is not
 * present, and on a hybrid device the thumb is the more deliberate signal.
 */
export function readIntent() {
  if (stick) {
    intent.moveX = stick.x;
    intent.moveZ = stick.z;
    return intent;
  }

  // Screen up is -Z with the camera fixed at yaw 0. The 3rd-person build had
  // W as +Z because the sim negated it through `forwardFromYaw`; nothing
  // negates it now, so the sign lives here.
  let x = 0;
  let z = 0;
  if (keys.w) z -= 1;
  if (keys.s) z += 1;
  if (keys.d) x += 1;
  if (keys.a) x -= 1;

  const mag = Math.hypot(x, z);
  if (mag === 0) {
    intent.moveX = 0;
    intent.moveZ = 0;
    return intent;
  }

  // v1's scheme: walk by default, HOLD SHIFT to sprint. Held, never latched —
  // releasing Shift drops back to walk immediately, on every device.
  const target = keys.shift ? 1 : WALK_INPUT;
  intent.moveX = (x / mag) * target;
  intent.moveZ = (z / mag) * target;
  return intent;
}

/** True while any movement key or the stick is engaged. Drives the idle bob. */
export function isMoving() {
  return Math.abs(intent.moveX) + Math.abs(intent.moveZ) > 0.01;
}
